import { createContext, useCallback, useContext, useState } from "react";
import { Loader2, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { startGoogleLogin } from "@/lib/auth";
import { downloadReport, inr, usd } from "@/lib/purchases";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";

const PRODUCT_NAMES = {
  PDF_REPORT: "Full 20-Criteria PDF Report",
  DFY_WEBSITE_LAUNCH: "Publish Live Custom-Domain Website (48-Hr DFY Launch)",
  GROWTH_DOSSIER: "Growth Tier — 16-Chapter Investor Dossier",
  ELITE_CONCIERGE: "Elite Concierge",
};
const NEEDS_EVALUATION = ["PDF_REPORT", "DFY_WEBSITE_LAUNCH", "ELITE_CONCIERGE"];
const CheckoutCtx = createContext({ openCheckout: () => {} });
export const useCheckout = () => useContext(CheckoutCtx);

const Body = ({ state, onPay, busy }) => {
  const { priceFor, priceBooks } = useApp();
  const { product, guest, options, evaluationId, error } = state;
  const inrBook = priceBooks.find((p) => p.product_key === product && p.region === "IN");
  if (guest) return (
    <button type="button" onClick={() => startGoogleLogin()} className="iv-cta mt-2 rounded-full px-4 py-2 text-sm font-bold text-white" data-testid="checkout-modal-signin">
      Sign in with Google to continue
    </button>
  );
  if (NEEDS_EVALUATION.includes(product) && !evaluationId) return (
    <p className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-sm text-slate-300" data-testid="checkout-needs-evaluation">
      This unlock is tied to your own evaluation. Run your assessment first, then buy it from your report. <a href="/?stage=2" className="font-semibold text-cyan-300">Start my assessment</a>
    </p>
  );
  if (!options) return <Loader2 className="h-5 w-5 animate-spin text-cyan-300" data-testid="checkout-loading" />;
  const india = options.provider === "razorpay";
  return (
    <div className="space-y-3">
      {india && (
        <p className="text-xs text-slate-400" data-testid="checkout-inr-line">Charged in INR ({inr(inrBook)}) via Razorpay</p>
      )}
      {india && !options.available ? (
        <p className="flex items-start gap-2 rounded-xl border border-amber-400/30 bg-amber-500/10 p-3 text-sm text-amber-100" data-testid="checkout-india-coming-soon">
          <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" /> India payments coming soon. Razorpay checkout opens here as soon as it is live; nothing will be charged today.
        </p>
      ) : (
        <>
          <p className="flex items-center gap-2 text-xs text-slate-400"><ShieldCheck className="h-4 w-4 text-emerald-300" /> Secure hosted checkout · amount set by our server ({usd(priceFor(product))})</p>
          {error && <p className="text-xs text-rose-300" role="alert" data-testid="checkout-error">{error}</p>}
          <button type="button" disabled={busy} onClick={onPay} className="iv-cta inline-flex w-full items-center justify-center gap-2 rounded-full py-2.5 text-sm font-bold text-white disabled:opacity-60" data-testid="checkout-pay-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Continue to secure checkout
          </button>
        </>
      )}
    </div>
  );
};

const openRazorpay = (res, navigate) => new Promise((resolve) => {
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => {
    const rz = new window.Razorpay({ key: res.razorpay.key_id, order_id: res.razorpay.order_id, amount: res.razorpay.amount, currency: "INR",
      name: "IdeaValidator360",
      handler: (r) => api.post("/razorpay/verify", { order_id: res.order_id, ...r }).finally(() => navigate(res.order_id)) });
    rz.open();
    resolve();
  };
  document.body.appendChild(s);
});

export function CheckoutProvider({ children }) {
  const { priceFor, user, requireMobile } = useApp();
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const openCheckout = useCallback(async (productKey, opts = {}) => {
    if (!user) { setOpen(true); return setState({ product: productKey, guest: true }); }
    if (!(await requireMobile())) return undefined;
    setOpen(true);
    setState({ product: productKey, evaluationId: opts.evaluationId, options: null });
    try {
      const { data } = await api.get("/checkout/options");
      return setState((s) => (s ? { ...s, options: data } : s));
    } catch {
      return setState((s) => (s ? { ...s, error: "Couldn't load checkout. Please try again." } : s));
    }
  }, [user, requireMobile]);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/checkout/orders", { product_key: state.product, evaluation_id: state.evaluationId, origin_url: window.location.origin });
      if (data.status === "redirect" && data.checkout_url) return window.location.assign(data.checkout_url);
      if (data.status === "redirect" && data.razorpay) return openRazorpay(data, (id) => window.location.assign(`/payment/success?order_id=${id}`));
      if (data.status === "already_owned") {
        toast.success("You already own this — no new charge.");
        if (state.product === "PDF_REPORT") await downloadReport(state.evaluationId);
        return setOpen(false);
      }
      return setState((s) => ({ ...s, options: { ...s.options, available: false } }));
    } catch (e) {
      if (e.response?.status === 428) return requireMobile();
      return setState((s) => ({ ...s, error: typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Checkout failed. Please try again." }));
    } finally {
      setBusy(false);
    }
  };

  const product = state?.product;
  return (
    <CheckoutCtx.Provider value={{ openCheckout }}>
      {children}
      <Dialog open={open && !!product} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="iv-glass max-w-[calc(100vw-2rem)] rounded-2xl border-white/10 bg-[#0B1021] sm:max-w-md" data-testid="checkout-modal">
          <DialogHeader className="text-left">
            <DialogTitle className="flex flex-wrap items-center gap-2 text-white" data-testid="checkout-modal-title">
              Checkout
              {state?.options?.test_mode && <span className="rounded-full border border-amber-400/50 bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold tracking-wider text-amber-200" data-testid="checkout-test-mode-badge">TEST MODE</span>}
            </DialogTitle>
            <DialogDescription className="text-slate-300" data-testid="checkout-product-summary">
              {PRODUCT_NAMES[product] ?? product} · <span className="font-mono font-bold text-white" data-testid="checkout-usd-price">{usd(product ? priceFor(product) : null)}</span>
            </DialogDescription>
          </DialogHeader>
          {state && <Body state={state} onPay={pay} busy={busy} />}
          {state?.options?.test_mode && <p className="text-[11px] text-slate-500" data-testid="checkout-test-note">Sandbox: use card 4242 4242 4242 4242, any future date and CVC. No real money moves.</p>}
        </DialogContent>
      </Dialog>
    </CheckoutCtx.Provider>
  );
}
